import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface QuotaStatus {
  canMakeRequest: boolean;
  requestsUsed: number;
  requestsLimit: number;
  percentUsed: number;
  resetAt: Date;
}

export function useApiQuota(quotaType: 'analysis' | 'measurement') {
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatus | null>(null);
  const [checking, setChecking] = useState(false);

  const checkQuota = useCallback(async (): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .single();

      if (!profile?.org_id) return false;

      // Get or create quota record
      const { data: quota, error } = await supabase
        .from('api_usage_quotas')
        .select('*')
        .eq('user_id', user.id)
        .eq('quota_type', quotaType)
        .gte('period_end', new Date().toISOString())
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Quota check error:', error);
        return true; // Allow request on error
      }

      // Create new quota record if doesn't exist or period expired
      if (!quota) {
        const now = new Date();
        const periodEnd = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour

        const { error: insertError } = await supabase
          .from('api_usage_quotas')
          .insert({
            user_id: user.id,
            org_id: profile.org_id,
            quota_type: quotaType,
            requests_used: 0,
            requests_limit: quotaType === 'analysis' ? 100 : 50, // Hourly limits
            period_start: now.toISOString(),
            period_end: periodEnd.toISOString()
          });

        if (insertError) {
          console.error('Failed to create quota:', insertError);
          return true; // Allow on error
        }

        setQuotaStatus({
          canMakeRequest: true,
          requestsUsed: 0,
          requestsLimit: quotaType === 'analysis' ? 100 : 50,
          percentUsed: 0,
          resetAt: periodEnd
        });

        return true;
      }

      // Check if quota exceeded
      const canMakeRequest = quota.requests_used < quota.requests_limit;
      const percentUsed = (quota.requests_used / quota.requests_limit) * 100;

      setQuotaStatus({
        canMakeRequest,
        requestsUsed: quota.requests_used,
        requestsLimit: quota.requests_limit,
        percentUsed,
        resetAt: new Date(quota.period_end)
      });

      // Warn when approaching limit
      if (percentUsed >= 90 && percentUsed < 100) {
        toast.warning(`API quota at ${percentUsed.toFixed(0)}%. Resets at ${new Date(quota.period_end).toLocaleTimeString()}`);
      } else if (!canMakeRequest) {
        toast.error(`API quota exceeded. Resets at ${new Date(quota.period_end).toLocaleTimeString()}`);
      }

      return canMakeRequest;
    } catch (error) {
      console.error('Quota check failed:', error);
      return true; // Allow on error
    }
  }, [quotaType]);

  const incrementQuota = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get current quota and increment
      const { data: quota } = await supabase
        .from('api_usage_quotas')
        .select('*')
        .eq('user_id', user.id)
        .eq('quota_type', quotaType)
        .gte('period_end', new Date().toISOString())
        .maybeSingle();

      if (quota) {
        await supabase
          .from('api_usage_quotas')
          .update({ requests_used: quota.requests_used + 1 })
          .eq('id', quota.id);
        
        // Refresh quota status
        await checkQuota();
      }
    } catch (error) {
      console.error('Failed to increment quota:', error);
    }
  }, [quotaType, checkQuota]);

  return {
    quotaStatus,
    checkQuota,
    incrementQuota,
    checking
  };
}
